/**
 * Variant Color Header
 * Changes the header and mobile menu background color based on selected variant color
 */

class VariantColorHeader {
  constructor() {
    this.header = document.querySelector('.header-wrapper--variant-color');
    if (!this.header) {
      console.log('VariantColorHeader: No header with variant-color class found');
      return;
    }

    this.menuDrawer = document.querySelector('.menu-drawer');
    this.initialized = false;

    this.init();
  }


  init() {
    const sectionId = this.header.dataset.sectionId;
    const colorDataEl = document.getElementById(`HeaderVariantColorData-${sectionId}`);

    if (!colorDataEl) {
      console.warn('VariantColorHeader: No color data element found for section', sectionId);
      return;
    }

    try {
      this.colorData = JSON.parse(colorDataEl.textContent);
      console.log('VariantColorHeader: Loaded color data', this.colorData);
      this.setupListeners();
      this.setInitialColor();
      this.initialized = true;
    } catch (e) {
      console.error('VariantColorHeader: Error parsing color data:', e);
    }
  }

  setupListeners() {
    // Method 1: Subscribe to pub/sub events (most reliable for Dawn theme)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      if (PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.handleVariantChange(event);
        });
      }

      // Also listen for option value selection change
      if (PUB_SUB_EVENTS.optionValueSelectionChange) {
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
          this.handleOptionChange(event);
        });
      }
    }

    // Method 2: Listen for variant selector changes (fallback)
    document.querySelectorAll('variant-selects, variant-radios').forEach((selector) => {
      selector.addEventListener('change', () => {
        setTimeout(() => this.handleVariantChangeFallback(), 50);
      });
    });

    // Method 3: Watch for URL changes
    window.addEventListener('popstate', () => {
      this.handleVariantChangeFallback();
    });

    // Method 4: MutationObserver for variant input changes
    const variantInput = document.querySelector('input[name="id"][form^="product-form"]');
    if (variantInput) {
      const observer = new MutationObserver(() => {
        this.handleVariantChangeFallback();
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }

    // Method 5: Listen for click on color swatches
    document.addEventListener('click', (e) => {
      // Check for swatch input or label click
      const swatchInput = e.target.closest('.swatch-input') ||
                          e.target.closest('[data-option-value-id]') ||
                          e.target.closest('.product-form__input--swatch label');

      if (swatchInput) {
        // Check if this is a color option by finding the parent fieldset
        const fieldset = swatchInput.closest('fieldset') || swatchInput.closest('.product-form__input');
        const legend = fieldset?.querySelector('legend, .form__label');
        const optionName = legend?.textContent?.replace(':', '').trim().toLowerCase() || '';

        const colorNames = ['color', 'colour', 'farbe', 'colore', 'couleur'];
        const isColorOption = colorNames.some(name => optionName.includes(name));

        if (isColorOption) {
          // Get color value from input or label
          const input = swatchInput.querySelector('input') || swatchInput;
          const colorName = input.value ||
                           input.getAttribute('value') ||
                           swatchInput.getAttribute('aria-label') ||
                           swatchInput.textContent?.trim();

          if (colorName) {
            console.log('VariantColorHeader: Swatch clicked, color:', colorName);
            setTimeout(() => this.updateHeaderColor(colorName), 50);
          }
        }
      }
    });
  }

  handleOptionChange(event) {
    if (!event.data) return;

    const { target } = event.data;
    if (!target) return;

    // Get the option name from the fieldset legend or label
    const fieldset = target.closest('fieldset') || target.closest('.product-form__input');
    if (!fieldset) return;

    const legend = fieldset.querySelector('legend, .form__label');
    const optionName = legend?.textContent?.replace(':', '').trim().toLowerCase() || '';

    // Check if this is a color option change
    const colorNames = ['color', 'colour', 'farbe', 'colore', 'couleur'];
    const isColorOption = colorNames.some(name => optionName.includes(name));

    if (isColorOption) {
      // Get the color value from the input
      const colorValue = target.value || target.getAttribute('value');
      if (colorValue) {
        console.log('VariantColorHeader: Color option changed to:', colorValue);
        this.updateHeaderColor(colorValue);
      }
    }
  }

  handleVariantChange(event) {
    if (!event.data?.variant) return;

    const variant = event.data.variant;
    const variantId = variant.id;
    const colorOptionIndex = this.colorData.colorOptionIndex;

    console.log('VariantColorHeader: variantChange event, variant:', variant);

    // Find the variant data with colorHex if available
    const variantData = this.colorData.variants.find(v => v.id === variantId);

    if (variantData?.colorHex) {
      console.log('VariantColorHeader: Using colorHex from variant data:', variantData.colorHex);
      this.applyColor(variantData.colorHex);
      return;
    }

    if (variantData?.color) {
      console.log('VariantColorHeader: Using color name from variant data:', variantData.color);
      this.updateHeaderColor(variantData.color);
      return;
    }

    // Try to get from variant.options array
    if (variant.options && variant.options[colorOptionIndex] !== undefined) {
      const selectedColorValue = variant.options[colorOptionIndex];
      console.log('VariantColorHeader: Using color from variant.options:', selectedColorValue);
      this.updateHeaderColor(selectedColorValue);
      return;
    }

    // Fallback: check currently selected swatch in DOM
    this.handleVariantChangeFallback();
  }

  handleVariantChangeFallback() {
    // Method 1: Try to get currently selected color swatch from DOM
    const colorNames = ['color', 'colour', 'farbe', 'colore', 'couleur'];

    document.querySelectorAll('.product-form__input--swatch, .product-form__input--pill').forEach(fieldset => {
      const legend = fieldset.querySelector('legend, .form__label');
      const optionName = legend?.textContent?.replace(':', '').trim().toLowerCase() || '';

      if (colorNames.some(name => optionName.includes(name))) {
        const checkedInput = fieldset.querySelector('input:checked');
        if (checkedInput) {
          console.log('VariantColorHeader: Fallback - found checked color input:', checkedInput.value);
          this.updateHeaderColor(checkedInput.value);
          return;
        }
      }
    });

    // Method 2: Try to get variant from URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      const variant = this.colorData.variants.find(v => v.id === parseInt(variantId));
      if (variant) {
        console.log('VariantColorHeader: Fallback - using URL variant:', variant.color);
        this.updateHeaderColor(variant.color);
        return;
      }
    }

    // Method 3: Try to get from hidden input
    const variantInput = document.querySelector('input[name="id"][form^="product-form"]');
    if (variantInput) {
      const variant = this.colorData.variants.find(v => v.id === parseInt(variantInput.value));
      if (variant) {
        console.log('VariantColorHeader: Fallback - using hidden input variant:', variant.color);
        this.updateHeaderColor(variant.color);
      }
    }
  }

  setInitialColor() {
    // Get initial color from data attribute
    const currentColorHandle = this.header.dataset.currentColor;
    console.log('VariantColorHeader: Setting initial color, currentColorHandle:', currentColorHandle);

    if (currentColorHandle) {
      // First try to find variant with matching handle or color name
      const variant = this.colorData.variants.find(v =>
        v.colorHandle === currentColorHandle ||
        v.color.toLowerCase() === currentColorHandle.toLowerCase() ||
        v.color.toLowerCase().replace(/\s+/g, '-') === currentColorHandle
      );

      if (variant) {
        console.log('VariantColorHeader: Found matching variant:', variant);
        // If variant has colorHex metafield, use it directly
        if (variant.colorHex) {
          this.applyColor(variant.colorHex);
          return;
        }
        this.updateHeaderColor(variant.color);
        return;
      }
    }

    // Fallback: use first variant's color
    if (this.colorData.variants.length > 0) {
      const firstVariant = this.colorData.variants[0];
      console.log('VariantColorHeader: Using first variant:', firstVariant);
      if (firstVariant.colorHex) {
        this.applyColor(firstVariant.colorHex);
      } else {
        this.updateHeaderColor(firstVariant.color);
      }
    }
  }

  // Direct color application (skips lookup)
  applyColor(hexColor) {
    if (!hexColor) return;
    console.log('VariantColorHeader: Applying color directly:', hexColor);

    const mainColor = this.mixColors(hexColor, '#1d2d44', 0.78);
    const softColor = this.mixColors(hexColor, '#ffffff', 0.38);
    const textColor = this.getContrastColor(mainColor);

    this.header.style.setProperty('--variant-source-color', hexColor);
    this.header.style.setProperty('--variant-bg-color', mainColor);
    this.header.style.setProperty('--variant-soft-color', softColor);
    this.header.style.setProperty('--variant-text-color', textColor);

    if (this.menuDrawer) {
      this.menuDrawer.style.setProperty('--variant-bg-color', mainColor);
      this.menuDrawer.style.setProperty('--variant-text-color', textColor);
      this.menuDrawer.classList.add('menu-drawer--variant-color');
    }

    // Shape now automatically follows via CSS: var(--variant-bg-color, ...)

    document.dispatchEvent(new CustomEvent('variant-color-change', {
      detail: { sourceColor: hexColor, bgColor: mainColor, softColor, textColor }
    }));
  }

  mixColors(sourceColor, targetColor, sourceWeight) {
    const source = this.hexToRgb(sourceColor);
    const target = this.hexToRgb(targetColor);
    if (!source || !target) return sourceColor;

    const targetWeight = 1 - sourceWeight;
    const red = Math.round(source.r * sourceWeight + target.r * targetWeight);
    const green = Math.round(source.g * sourceWeight + target.g * targetWeight);
    const blue = Math.round(source.b * sourceWeight + target.b * targetWeight);

    return `#${[red, green, blue].map(value => value.toString(16).padStart(2, '0')).join('')}`;
  }

  hexToRgb(hexColor) {
    if (!hexColor) return null;
    let hex = hexColor.replace('#', '').trim();
    if (hex.length === 3) {
      hex = hex.split('').map(character => character + character).join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(hex)) return null;

    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  normalizeColorName(color) {
    return color
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  findColorHex(colorValue) {
    const normalizedInput = this.normalizeColorName(colorValue);

    // 1. Check if it's already a hex color
    if (/^#[0-9a-f]{6}$/i.test(colorValue)) {
      return colorValue;
    }

    // 2. Check variant metafields for exact match
    const variant = this.colorData.variants.find(v =>
      v.color.toLowerCase() === colorValue.toLowerCase() ||
      v.colorHandle === colorValue.toLowerCase().replace(/\s+/g, '-')
    );

    if (variant?.colorHex) {
      return variant.colorHex;
    }

    // 3. Direct lookup in colorMap
    const colorMap = this.colorData.colorMap;

    // Try exact match
    if (colorMap[normalizedInput]) {
      return colorMap[normalizedInput];
    }

    // Try with common variations
    const variations = [
      normalizedInput,
      normalizedInput.replace('grey', 'gray'),
      normalizedInput.replace('gray', 'grey'),
    ];

    for (const variation of variations) {
      if (colorMap[variation]) {
        return colorMap[variation];
      }
    }

    // 4. Partial match - check if input contains a color name
    for (const [colorName, hex] of Object.entries(colorMap)) {
      if (normalizedInput.includes(colorName) || colorName.includes(normalizedInput)) {
        return hex;
      }
    }

    // 5. Check for color words in multi-word values (e.g., "Midnight Blue")
    const words = colorValue.toLowerCase().split(/\s+/);
    for (const word of words) {
      const normalized = this.normalizeColorName(word);
      if (colorMap[normalized]) {
        return colorMap[normalized];
      }
    }

    return null;
  }

  updateHeaderColor(colorValue) {
    if (!colorValue) return;

    const bgColor = this.findColorHex(colorValue);

    if (bgColor) {
      this.applyColor(bgColor);
    } else {
      // Reset to defaults
      this.resetColors();
    }
  }

  resetColors() {
    this.header.style.removeProperty('--variant-source-color');
    this.header.style.removeProperty('--variant-bg-color');
    this.header.style.removeProperty('--variant-soft-color');
    this.header.style.removeProperty('--variant-text-color');

    if (this.menuDrawer) {
      this.menuDrawer.style.removeProperty('--variant-bg-color');
      this.menuDrawer.style.removeProperty('--variant-text-color');
      this.menuDrawer.classList.remove('menu-drawer--variant-color');
    }
  }

  getContrastColor(hexColor) {
    // Handle shorthand hex
    let hex = hexColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }

    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;

    // Calculate relative luminance (WCAG formula)
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Return dark text for light backgrounds, light text for dark backgrounds
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  }
}

// Initialize when DOM is ready
function initVariantColorHeader() {
  // Small delay to ensure pub/sub is available
  setTimeout(() => {
    new VariantColorHeader();
  }, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVariantColorHeader);
} else {
  initVariantColorHeader();
}
